/**
 * Shared persistence helpers for the Lumi agent chat.
 *
 * Kept as its own module so both `LumiAgentPanel` (which reads/writes) and
 * `useLumi` (which garbage-collects foreign scopes on identity change) agree
 * on the key format without either importing the other.
 */

export const STORAGE_PREFIX = 'lumi-agent-chat:';

export interface StorageKeys {
    messages: string;
    draft: string;
    mode: string;
}

function scopePrefix(tenant: string, userId: string): string {
    // v2: chats saved under AI SDK 5 aren't replayed into v7; purgeForeignScopes deletes the v1 keys.
    return `${STORAGE_PREFIX}v2:${tenant}:${userId}:`;
}

export function makeStorageKeys(tenant: string, userId: string): StorageKeys {
    const scope = scopePrefix(tenant, userId);
    return { messages: `${scope}messages`, draft: `${scope}draft`, mode: `${scope}mode` };
}

/**
 * Delete any Lumi-agent chat state on disk that doesn't belong to the current
 * tenant+user scope. Called from `useLumi` — which is mounted whenever the
 * app's global command palette is (i.e. whenever the user is authenticated) —
 * so foreign scopes get purged on every session boot AND on identity change,
 * whether or not the panel is ever opened. This is the only cleanup path;
 * messages can contain sensitive account data, so shared devices shouldn't
 * accumulate stale scopes.
 */
export function purgeForeignScopes(tenant: string, userId: string): void {
    const currentScope = scopePrefix(tenant, userId);
    try {
        for (let i = localStorage.length - 1; i >= 0; i--) {
            const key = localStorage.key(i);
            if (key?.startsWith(STORAGE_PREFIX) && !key.startsWith(currentScope)) {
                localStorage.removeItem(key);
            }
        }
    } catch {
        // Best-effort.
    }
}
